<template>
  <div class="contents">
    <el-card>
      <div slot="header" class="card-header">
        <span>{{ $t('contents.title') }}</span>
        <el-button type="primary" size="small" @click="handleAdd">{{ $t('contents.add') }}</el-button>
      </div>

      <!-- 搜索栏 -->
      <el-form inline class="search-form">
        <el-form-item :label="$t('contents.keyword')">
          <el-input v-model="search.keyword" :placeholder="$t('contents.kwPlaceholder')" clearable @clear="handleSearch"></el-input>
        </el-form-item>
        <el-form-item :label="$t('contents.language')">
          <el-select v-model="search.language" :placeholder="$t('contents.languageAll')" clearable @change="handleSearch" style="width: 140px;">
            <el-option :label="$t('contents.languageAll')" value=""></el-option>
            <el-option label="中文" value="zh"></el-option>
            <el-option label="English" value="en"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item :label="$t('contents.missingFilter')">
          <el-switch v-model="search.missingOnly" @change="handleSearch"></el-switch>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleSearch">{{ $t('contents.search') }}</el-button>
          <el-button @click="resetSearch">{{ $t('contents.reset') }}</el-button>
        </el-form-item>
      </el-form>

      <!-- 内容卡片网格（按双语组聚合展示） -->
      <el-row :gutter="20">
        <el-col :span="12" v-for="group in groupedList" :key="group.key">
          <el-card shadow="hover" class="content-card group-card" :body-style="{ padding: '0' }">
            <div class="group-header">
              <span class="group-label">{{ $t('contents.bilingualGroup') }}</span>
              <el-tag type="info" size="mini">{{ $t('contents.groupTag', { id: group.id }) }}</el-tag>
            </div>
            <div class="lang-versions">
              <div
                class="lang-version"
                v-for="lang in ['zh', 'en']"
                :key="lang"
              >
                <template v-if="getVersion(lang, group)">
                  <h5 class="lang-version-title" :title="getVersion(lang, group).title">{{ getVersion(lang, group).title }}</h5>
                </template>
                <div class="lang-version-head">
                  <el-tag :type="lang === 'en' ? 'warning' : 'primary'" size="mini" effect="dark">
                    {{ lang === 'en' ? 'EN' : '中文' }}
                  </el-tag>
                  <el-tag :type="getVersion(lang, group) && getVersion(lang, group).status === 1 ? 'success' : 'info'" size="mini">
                    {{ getVersion(lang, group) ? (getVersion(lang, group).status === 1 ? $t('contents.published') : $t('contents.draft')) : $t('contents.notConfigured') }}
                  </el-tag>
                </div>
                <template v-if="getVersion(lang, group)">
                  <div class="content-cover">
                    <img v-if="getVersion(lang, group).coverImage" :src="getFullUrl(getVersion(lang, group).coverImage)" alt="">
                    <div v-else class="no-cover">
                      <i class="el-icon-document"></i>
                    </div>
                  </div>
                  <p class="content-desc">{{ getVersion(lang, group).description || $t('contents.noDesc') }}</p>
                  <div class="content-actions">
                    <el-button type="text" size="small" @click="handlePreview(getVersion(lang, group))">{{ $t('contents.preview') }}</el-button>
                    <el-button type="text" size="small" @click="handleEdit(getVersion(lang, group))">{{ $t('contents.edit') }}</el-button>
                    <el-button type="text" size="small" style="color: #F56C6C;" @click="handleDelete(getVersion(lang, group))">{{ $t('contents.delete') }}</el-button>
                  </div>
                </template>
                <div v-else class="lang-empty">
                  {{ $t('contents.noVersion', { lang: lang === 'en' ? 'EN' : '中文' }) }}
                  <el-button type="text" size="small" @click="handleAddPair(group, lang)">{{ $t('contents.createVersion') }}</el-button>
                </div>
              </div>
            </div>
          </el-card>
        </el-col>
      </el-row>

      <!-- 分页 -->
      <el-pagination
        class="pagination"
        @current-change="handlePageChange"
        :current-page="page"
        :page-size="pageSize"
        :total="total"
        layout="total, prev, pager, next"
      ></el-pagination>
    </el-card>

    <!-- 预览对话框 - 公众号推文风格 -->
    <el-dialog :title="$t('contents.previewTitle')" :visible.sync="previewVisible" width="500px" top="5vh">
      <div v-if="previewItem" class="preview-wrapper">
        <div class="preview-header">
          <h3 class="preview-title">{{ previewItem.title }}</h3>
          <p class="preview-desc">{{ previewItem.description }}</p>
        </div>
        <div v-if="previewItem.videoUrl || previewItem.externalUrl" style="margin: 15px 0;">
          <video :src="getFullUrl(previewItem.externalUrl || previewItem.videoUrl)" controls style="width: 100%; max-height: 400px; border-radius: 8px;"></video>
        </div>
        <div v-if="previewItem.textContent" v-html="previewItem.textContent" class="preview-content"></div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getList, deleteContent, createPairContent } from '@/api/contents'
import { getFullUrl as _getFullUrl } from '@/utils'

export default {
  name: 'Contents',
  data() {
    return {
      list: [],
      groupedList: [],
      loading: false,
      page: 1,
      pageSize: 12,
      total: 0,
      search: {
        keyword: '',
        type: '',
        language: '',
        missingOnly: false
      },
      previewVisible: false,
      previewItem: null
    }
  },
  created() {
    this.loadData()
  },
  methods: {
    getFullUrl(url) {
      return _getFullUrl(url)
    },
    // 加载内容列表
    loadData() {
      this.loading = true
      getList({
        page: this.page,
        pageSize: this.pageSize,
        ...this.search
      }).then(res => {
        this.list = res.data.list || []
        this.total = res.data.total || 0
        this.buildGroups()
        // 仅看缺失版本：过滤掉已同时包含中、英两版的组
        if (this.search.missingOnly) {
          this.groupedList = this.groupedList.filter(g => !(g.versions.zh && g.versions.en))
        }
      }).catch(() => {
        this.$message.error(this.$t('contents.loadFail'))
      }).finally(() => {
        this.loading = false
      })
    },
    // 将课程按 languageGroupId 聚合为双语组（未配对的单篇自成一组）
    //
    // 必须用 Map 而非普通对象：后端已按「组内最大 id 倒序」返回（新建课程在最前），
    // 但 languageGroupId 是整数（新建课程会设为自身 id），JS 普通对象对整数 key
    // 会强制按数字升序重排、忽略插入顺序，导致新建课程被甩到列表最底部。
    // Map 严格保持插入顺序，可保住后端排序。
    buildGroups() {
      const map = new Map()
      this.list.forEach(item => {
        const gid = item.languageGroupId || item.id
        if (!map.has(gid)) {
          map.set(gid, {
            key: gid,
            id: gid,
            title: item.title,
            versions: {}
          })
        }
        map.get(gid).versions[item.language] = item
      })
      this.groupedList = Array.from(map.values())
    },
    // 取某组内指定语言的版本（无则返回 null）
    getVersion(lang, group) {
      return group.versions[lang] || null
    },
    // 为某组补建缺失的语言版本：复制已有版本生成配对语言版
    handleAddPair(group, lang) {
      const existing = group.versions.zh || group.versions.en
      if (!existing) return
      this.$confirm(
        this.$t('contents.createPairConfirm', { lang: lang === 'en' ? 'EN' : '中文' }),
        this.$t('common.tip'),
        { confirmButtonText: this.$t('common.confirm'), cancelButtonText: this.$t('common.cancel'), type: 'warning' }
      ).then(() => {
        createPairContent(existing.id).then(res => {
          this.$message.success(res.msg || this.$t('contents.createPairSuccess'))
          this.loadData()
        }).catch(() => {
          this.$message.error(this.$t('contents.createPairFailed'))
        })
      }).catch(() => {})
    },
    // 搜索
    handleSearch() {
      this.page = 1
      this.loadData()
    },
    // 重置搜索
    resetSearch() {
      this.search = { keyword: '', type: '' }
      this.page = 1
      this.loadData()
    },
    // 新增内容
    handleAdd() {
      this.$router.push('/contents/edit')
    },
    // 编辑内容
    handleEdit(item) {
      this.$router.push('/contents/edit/' + item.id)
    },
    // 预览内容
    handlePreview(item) {
      this.previewItem = item
      this.previewVisible = true
    },
    // 删除内容
    async handleDelete(item) {
      try {
        await this.$confirm(this.$t('contents.deleteConfirm'), this.$t('common.tip'), {
          confirmButtonText: this.$t('common.confirm'),
          cancelButtonText: this.$t('common.cancel'),
          type: 'warning'
        })
        deleteContent(item.id).then(() => {
          this.$message.success(this.$t('contents.deleteSuccess'))
          this.loadData()
        }).catch(() => {
          this.$message.error(this.$t('contents.deleteFail'))
        })
      } catch (e) {
        // 取消
      }
    },
    // 分页
    handlePageChange(page) {
      this.page = page
      this.loadData()
    }
  }
}
</script>

<style scoped>
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: bold;
  font-size: 16px;
}
.search-form {
  margin-bottom: 15px;
}
.content-card {
  margin-bottom: 20px;
  min-height: 320px;
  display: flex;
  flex-direction: column;
}
.content-card ::v-deep .el-card__body {
  flex: 1;
  display: flex;
  flex-direction: column;
}
.content-cover {
  height: 120px;
  overflow: hidden;
  background: #f5f7fa;
  flex-shrink: 0;
}
.content-cover img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.no-cover {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #c0c4cc;
  font-size: 40px;
}
.content-info {
  padding: 12px;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.content-tags {
  margin-bottom: 8px;
  flex-shrink: 0;
}
.content-tags .el-tag {
  margin-right: 5px;
}
.content-title {
  margin: 0 0 8px;
  font-size: 14px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex-shrink: 0;
}
.content-desc {
  margin: 0;
  font-size: 12px;
  color: #909399;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  flex-shrink: 0;
  min-height: 36px;
  /* 严格固定 2 行高度，配合标题区一起避免撑开卡片 */
  height: 36px;
}
.content-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: auto;
  padding-top: 10px;
  flex-shrink: 0;
}
/* 双语组卡片 */
.group-card {
  margin-bottom: 20px;
}
.group-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  border-bottom: 1px solid #f0f0f0;
  background: #fafafa;
}
.group-label {
  font-size: 13px;
  font-weight: 600;
  color: #909399;
}
.lang-version-title {
  margin: 0 0 8px;
  font-size: 14px;
  font-weight: 600;
  color: #1a1a2e;
  line-height: 1.35;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  /* 严格固定 2 行高度：避免超长标题把底部按钮挤出卡片 */
  min-height: 40px;
  max-height: 40px;
  flex-shrink: 0;
  word-break: break-all;
}
.lang-versions {
  display: flex;
}
.lang-version {
  flex: 1;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.lang-version:first-child {
  border-right: 1px solid #f0f0f0;
}
.lang-version-head {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 10px;
}
.lang-empty {
  font-size: 13px;
  color: #999;
  padding: 20px 0;
  text-align: center;
  flex-direction: column;
  display: flex;
  align-items: center;
  gap: 6px;
}
.pagination {
  margin-top: 20px;
  text-align: right;
}
/* 预览 - 公众号推文风格 */
.preview-wrapper {
  max-height: 70vh;
  overflow-y: auto;
}
.preview-header {
  text-align: center;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid #eee;
}
.preview-title {
  font-size: 22px;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0 0 10px;
  line-height: 1.4;
}
.preview-desc {
  font-size: 14px;
  color: #999;
  margin: 0;
}
.preview-content {
  font-size: 15px;
  line-height: 1.8;
  color: #333;
  word-wrap: break-word;
}
.preview-content >>> h1 {
  font-size: 22px;
  font-weight: 700;
  margin: 20px 0 10px;
  color: #1a1a2e;
}
.preview-content >>> h2 {
  font-size: 19px;
  font-weight: 700;
  margin: 18px 0 8px;
  color: #1a1a2e;
}
.preview-content >>> h3 {
  font-size: 17px;
  font-weight: 700;
  margin: 16px 0 8px;
  color: #1a1a2e;
}
.preview-content >>> blockquote {
  border-left: 4px solid #667eea;
  padding: 10px 16px;
  margin: 12px 0;
  background: #f8f9ff;
  border-radius: 0 4px 4px 0;
  color: #555;
}
.preview-content >>> img {
  max-width: 100%;
  border-radius: 4px;
  margin: 8px 0;
}
.preview-content >>> a {
  color: #667eea;
  text-decoration: none;
}
</style>
